import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface Slide {
  type: 'image' | 'logo';
  src?: string;
  alt: string;
}

const slides: Slide[] = [
  { type: 'logo', alt: 'KPC Urban Korean Food Logo' },
  { type: 'image', src: '/images/slider-kbomb.jpg', alt: 'K-Bomb Fried Chicken' },
  { type: 'image', src: '/images/slider-bibimbap.jpg', alt: 'Bibimbap' },
  { type: 'image', src: '/images/slider-tatertots.jpg', alt: 'Bulgogi Tater Tots' },
  { type: 'image', src: '/images/slider-sandwich.jpg', alt: 'K-Style Chicken Sandwich' },
  { type: 'image', src: '/images/seoul-night-1.jpg', alt: 'Seoul Night' },
];

export default function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning]);

  const prevSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning]);

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 800);
  };

  // Auto-advance slides
  useEffect(() => {
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [nextSlide]);

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Slides Container */}
      <div 
        className="flex h-full transition-transform duration-800 ease-in-out"
        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <div 
            key={index}
            className="w-full h-full flex-shrink-0 relative"
          >
            {slide.type === 'logo' ? (
              // Logo Slide - Integrated with background
              <div className="w-full h-full flex items-center justify-center relative overflow-hidden">
                {/* Animated background pattern */}
                <div className="absolute inset-0">
                  <div 
                    className="absolute inset-0 opacity-10"
                    style={{
                      backgroundImage: `
                        radial-gradient(circle at 20% 30%, rgba(255, 215, 0, 0.4) 0%, transparent 40%),
                        radial-gradient(circle at 80% 70%, rgba(220, 20, 60, 0.3) 0%, transparent 40%),
                        radial-gradient(circle at 50% 50%, rgba(255, 165, 0, 0.2) 0%, transparent 50%)
                      `,
                    }}
                  />
                  {/* Subtle grid pattern */}
                  <div 
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                      backgroundImage: `
                        linear-gradient(rgba(255, 215, 0, 0.5) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(255, 215, 0, 0.5) 1px, transparent 1px)
                      `,
                      backgroundSize: '60px 60px',
                    }}
                  />
                </div>
                
                {/* Logo container with integrated effects */}
                <div className="relative flex flex-col items-center">
                  {/* Outer glow ring */}
                  <div 
                    className="absolute w-[120%] h-[120%] rounded-full blur-3xl opacity-20"
                    style={{
                      background: 'radial-gradient(circle, rgba(255, 215, 0, 0.6) 0%, rgba(220, 20, 60, 0.3) 50%, transparent 70%)',
                    }}
                  />
                  
                  {/* Inner glow */}
                  <div 
                    className="absolute w-[80%] h-[80%] rounded-full blur-2xl opacity-30"
                    style={{
                      background: 'radial-gradient(circle, rgba(255, 215, 0, 0.8) 0%, transparent 60%)',
                    }}
                  />
                  
                  {/* Logo image with seamless integration */}
                  <div className="relative">
                    <img 
                      src="/images/logo.png" 
                      alt={slide.alt}
                      className="relative z-10 h-40 sm:h-52 md:h-64 lg:h-72 w-auto object-contain"
                      style={{
                        filter: 'drop-shadow(0 0 30px rgba(255, 215, 0, 0.3)) drop-shadow(0 0 60px rgba(220, 20, 60, 0.2))',
                      }}
                    />
                  </div>
                  
                  {/* Tagline below logo */}
                  <div className="mt-6 text-center relative z-10">
                    <p className="text-[#FFD700]/80 text-sm sm:text-base tracking-[0.3em] font-['Orbitron'] uppercase">
                      Urban Korean Food
                    </p>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="w-8 h-[1px] bg-gradient-to-r from-transparent to-[#FFD700]/50" />
                      <span className="text-white/40 text-xs tracking-wider">KISSIMMEE, FL</span>
                      <span className="w-8 h-[1px] bg-gradient-to-l from-transparent to-[#FFD700]/50" />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              // Image Slide
              <div className="w-full h-full relative">
                <img 
                  src={slide.src} 
                  alt={slide.alt}
                  className="w-full h-full object-cover"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/50" />
                
                {/* Korean text overlay */}
                <div className="absolute bottom-8 left-8 right-8">
                  <p className="text-[#FFD700]/60 text-lg font-['Orbitron'] tracking-wider">
                    {slide.alt}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-[#FFD700]/30 flex items-center justify-center hover:bg-[#FFD700]/20 transition-all z-20 group"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6 text-[#FFD700] group-hover:scale-110 transition-transform" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm border border-[#FFD700]/30 flex items-center justify-center hover:bg-[#FFD700]/20 transition-all z-20 group"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6 text-[#FFD700] group-hover:scale-110 transition-transform" />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? 'w-8 bg-[#FFD700]' 
                : 'w-2 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Slide Counter */}
      <div className="absolute top-6 right-6 z-20">
        <span className="text-[#FFD700] font-['Orbitron'] text-sm">
          {String(currentSlide + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
        </span>
      </div>
    </div>
  );
}
